import { HttpErrorResponse, HttpProgressEvent } from '@angular/common/http';

import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';

import { FileHelper } from '@helpers/file.helper';
import { Document } from '@models/document';

import { UploadFile } from './upload-file';
import { UPLOAD_FILE_CONFIG } from './upload-file-config.constant';
import { UploadFileStatus } from './upload-file-status.enum';

export abstract class UploadFileService {
  filesInQueue: UploadFile[] = [];

  private readonly refreshViewOnUploadEvent: Subject<void> = new Subject();
  private readonly uploadQueue: BehaviorSubject<UploadFile[]> = new BehaviorSubject(undefined);
  private readonly acceptedTypes = UPLOAD_FILE_CONFIG.extendedAcceptedFileTypes;
  private readonly maximumSize = UPLOAD_FILE_CONFIG.maximumSize;

  get hasPendingUpload(): boolean {
    return this.filesInQueue.some(
      (file: UploadFile) =>
        file.uploadStatus === UploadFileStatus.NOT_STARTED || file.uploadStatus === UploadFileStatus.IN_PROGRESS
    );
  }

  get refreshViewOnUploadEvent$(): Observable<void> {
    return this.refreshViewOnUploadEvent.asObservable();
  }

  get uploadQueue$(): Observable<UploadFile[]> {
    return this.uploadQueue.asObservable();
  }

  addFilesToQueue(files: FileList): void {
    const uploadFiles = this.convertFilesToUploadFiles(files);
    this.filesInQueue = [...uploadFiles, ...this.filesInQueue];
    this.uploadQueue.next(uploadFiles);
  }

  clearQueue(): void {
    this.filesInQueue = [];
    this.uploadQueue.next([]);
  }

  observeUploadQueue(): Observable<any> {
    return this.uploadQueue$.pipe(
      filter((files: UploadFile[]) => !!files && files.length > 0),
      mergeMap((uploadFiles: UploadFile[]) => {
        const filesObs = uploadFiles.map((uploadFile: UploadFile) => this.uploadFile(uploadFile));
        return forkJoin(filesObs);
      })
    );
  }

  calculateUploadProgress(progressEvent: HttpProgressEvent): number {
    return Math.round((100 * progressEvent.loaded) / progressEvent.total);
  }

  private convertFilesToUploadFiles(files: FileList): UploadFile[] {
    return Array.from(files).map((file: UploadFile) => {
      file.uploadStatus = UploadFileStatus.NOT_STARTED;
      file.progress = 0;
      return file;
    });
  }

  private hasIncorrectSize(file: File): boolean {
    if (!this.maximumSize) {
      return false;
    }
    return this.maximumSize && file.size > this.maximumSize * 1000000;
  }

  private isAcceptedType(file: File): boolean {
    if (this.acceptedTypes.length === 0) {
      return true;
    }
    return FileHelper.isAcceptedType(this.acceptedTypes, file);
  }

  private uploadErrorMessage(error: any): string {
    return !!error && error instanceof HttpErrorResponse && error.error && error.error.error_description
      ? error.error.error_description
      : FileHelper.getInvalidFileErrorMessage();
  }

  private uploadFile(fileToUpload: UploadFile): Observable<any> {
    if (!this.isAcceptedType(fileToUpload) || this.hasIncorrectSize(fileToUpload)) {
      return this.uploadFileError(fileToUpload);
    }
    return of(fileToUpload).pipe(
      map((uploadFile: UploadFile) => {
        uploadFile.uploadStatus = UploadFileStatus.IN_PROGRESS;
        return uploadFile;
      }),
      mergeMap((file: File) => this.uploadDocumentObs(file)),
      tap((result: Document | HttpProgressEvent) => {
        if (result instanceof Document) {
          fileToUpload.uploadStatus = UploadFileStatus.COMPLETED;
          return;
        }
        fileToUpload.progress = this.calculateUploadProgress(result);
      }),
      catchError(error => this.uploadFileError(fileToUpload, error))
    );
  }

  private uploadFileError(fileToUpload: UploadFile, error?: any): Observable<UploadFile> {
    fileToUpload.error = this.uploadErrorMessage(error);
    fileToUpload.progress = 0;
    fileToUpload.uploadStatus = UploadFileStatus.ON_ERROR;
    return of(fileToUpload);
  }

  abstract uploadDocumentObs(file: File): Observable<Document | HttpProgressEvent>;
}
